import React, { Component } from 'react'
import { produce } from 'immer'
import { compact, find, map, reject, size } from 'lodash'
import { PalletData } from '../apis/pallet.api'
import { CaseData } from '../apis/inventoryCase.api'
import ContentAside from '../components/ContentAside'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import ItemCard from '../components/ItemCard'
import CasePicker from '../components/CasePicker'
import CaseTransferForm from '../partials/CaseTransferForm/CaseTransferForm'

interface Props {}

interface State {
  selectedCases: (CaseData | null)[]
  sourcePallet: PalletData | null
  consumeCase: CaseData | null
}

export default class CaseTransfer
  extends Component<Props, State> {
  initialState: State = {
    selectedCases: [],
    sourcePallet: null,
    consumeCase: null
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleSourcePalletChange = this.handleSourcePalletChange.bind(this)
    this.handleConsumeCaseChange = this.handleConsumeCaseChange.bind(this)
    this.handleCaseScan = this.handleCaseScan.bind(this)
    this.handleResetForm = this.handleResetForm.bind(this)
  }

  get casePicker () {
    const { sourcePallet } = this.state
    return sourcePallet && sourcePallet.positions && sourcePallet.item &&
      <CasePicker
        cases={sourcePallet.item.cpp}
        availables={sourcePallet.positions}
        selectedPositions={this.selectedCasePositions}
        selectedCases={this.state.selectedCases} />
  }

  get availableCasesOnPallet () {
    const { sourcePallet } = this.state
    if (!sourcePallet || !sourcePallet.item) return
    const { positions, item } = sourcePallet
    return `${size(positions)}/${item.cpp}`
  }

  get sourcePalletCard () {
    const { sourcePallet } = this.state
    return sourcePallet && sourcePallet.item &&
      <ItemCard
        code={sourcePallet.item.code}
        description={sourcePallet.item.description}
        cpp={sourcePallet.item.cpp}
        qpc={sourcePallet.item.qpc}
        availableCasesOnPallet={this.availableCasesOnPallet} />
  }

  get consumeCaseCard () {
    const { consumeCase } = this.state
    return consumeCase && consumeCase.pallet && consumeCase.pallet.item &&
      <ItemCard
        code={consumeCase.pallet.item.code}
        description={consumeCase.pallet.item.description}
        cpp={consumeCase.pallet.item.cpp}
        qpc={consumeCase.pallet.item.qpc}
        availableQuantitesOnCase={consumeCase.quantity} />
  }

  get selectedCaseIds () {
    const ids = map(this.state.selectedCases, selectedCase => {
      return selectedCase ? selectedCase.id : null
    })
    return compact(ids)
  }

  get selectedCasePositions () {
    const positions = map(this.state.selectedCases, selectedCase => {
      return selectedCase ? selectedCase.position : null
    })
    return compact(positions)
  }

  handleSourcePalletChange (sourcePallet: PalletData | null) {
    this.setState({ sourcePallet })
  }

  handleConsumeCaseChange (consumeCase: CaseData | null) {
    this.setState({ consumeCase })
  }

  canPushSelectedCases (inventoryCase: CaseData) {
    const { sourcePallet } = this.state
    if (!sourcePallet) return false
    if (inventoryCase.palletId !== sourcePallet.id) return false
    return true
  }

  pushSelectedCases (inventoryCase: CaseData) {
    const selectedCases = produce(
      this.state.selectedCases,
      selectedCases => {
        const isSelect = find(selectedCases, selectedCase => {
          return selectedCase && selectedCase.id === inventoryCase.id
        })
        if (isSelect) {
          selectedCases = reject(selectedCases, selectedCase => {
            if (!selectedCase) return false
            return selectedCase.id === inventoryCase.id
          })
        }
        selectedCases[inventoryCase.position] = inventoryCase
      })
    this.setState({ selectedCases })
  }

  handleCaseScan (inventoryCase: CaseData) {
    if (this.canPushSelectedCases(inventoryCase)) {
      this.pushSelectedCases(inventoryCase)
    }
  }

  handleResetForm () {
    this.setState(this.initialState)
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <CaseTransferForm
            selectedCases={this.selectedCaseIds}
            onSourcePalletChange={this.handleSourcePalletChange}
            onConsumeCaseChange={this.handleConsumeCaseChange}
            onCaseScan={this.handleCaseScan}
            onResetForm={this.handleResetForm}>
            {this.casePicker}
          </CaseTransferForm>
        </ContentMain>
        <ContentAside layout="oneThird">
          {this.sourcePalletCard}
          {this.consumeCaseCard}
        </ContentAside>
      </ContentLayout>
    )
  }
}
